import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import MemberListPanel from 'dashboard/views/DashboardTeamShowView/MemberListPanel';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import { HStack } from 'design-system/components/Stack';
import { Label, Span } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

const TeamModulesWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: ${Spacing[5]};
  padding: ${Spacing[7]};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: ${Spacing[2]};
`;

interface EditTeamFormProps {
  userIdToRoleNames: Record<string, string[]>;
  team: Team;
  teamUserIds: string[];
  users: User[];
  deleteTeam: (teamId: string) => Promise<void>;
  updateTeam: (team: FormEditTeamData) => Promise<void>;
}

const EditTeamForm = ({
  userIdToRoleNames,
  team,
  teamUserIds,
  users,
  deleteTeam,
  updateTeam,
}: EditTeamFormProps) => {
  const [deleteTeamModal, { enable: showDeleteTeamModal, disable: hideDeleteTeamModal }] =
    useToggle(false);
  const {
    register,
    getValues,
    setValue,
    reset,
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting, isSubmitted, errors, isValid },
  } = useFormSecurely<FormEditTeamData>({
    defaultValues: {
      name: team.attributes.name,
      'user-ids': teamUserIds,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, getValues, reset]);

  const handleChangeTeamName = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('name', event.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <>
      <NavConfirmPrompt
        prevent={isDirty && team.attributes.status === 'active'}
        message='Are you sure you want to leave?'
      >
        Changes you made will not be saved.
      </NavConfirmPrompt>
      <form onSubmit={handleSubmit(updateTeam)}>
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBack to='/dashboard/teams' />
            <Span>All teams</Span>
          </HorizontalContainer>
          <HStack>
            <Button
              id='teams-table__delete-team'
              variant='danger'
              onClick={showDeleteTeamModal}
              disabled={isSubmitting}
            >
              Delete
            </Button>
            <Button
              id='teams-table__save'
              type='submit'
              disabled={!isDirty || isSubmitting || !isValid}
            >
              Save
            </Button>
          </HStack>
        </FullScreenHeaderContainer>
        <TeamModulesWrapper>
          <Card>
            <Card.Body>
              <Controller
                control={control}
                name='user-ids'
                render={({ field: { onChange, value } }) => (
                  <MemberListPanel
                    userIdToRoleNames={userIdToRoleNames}
                    teamUserIds={value}
                    users={users}
                    setUserIds={onChange}
                  />
                )}
              />
            </Card.Body>
          </Card>
          <div>
            <Card>
              <Card.Body>
                <TableTitleContainer>
                  <Card.Title>Summary</Card.Title>
                </TableTitleContainer>
                <Label size='md' weight='bold'>
                  Team name
                </Label>
                <Input
                  {...register('name', {
                    required: 'Teams must have a name.',
                  })}
                  onChange={handleChangeTeamName}
                />
                {errors['name'] && <FormError>{(errors['name'] as any)?.message}</FormError>}
              </Card.Body>
            </Card>
          </div>
        </TeamModulesWrapper>
        <ModalWrapper isOpen={deleteTeamModal} handleCloseModal={hideDeleteTeamModal}>
          <ConfirmationModal
            cancelAction={hideDeleteTeamModal}
            confirmAction={() => deleteTeam(team.id)}
            confirmActionText='Delete'
          >
            Are you sure you want to delete this team? This action is irreversable.
          </ConfirmationModal>
        </ModalWrapper>
      </form>
    </>
  );
};

export interface FormEditTeamData {
  name: string;
  'user-ids': string[];
}

export default EditTeamForm;
